import { observer, useLocalObservable } from 'mobx-react';
import { DETAILS_POUP_TABS } from './constants/tabs';
import { observable, runInAction } from 'mobx';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useStore } from '../../../store/store';
import { API_ENDPOINTS } from '../../../api/endpoints';
import { DOWNLOAD_LIMIT_EXCEEDED_CODE } from '../../../utils/constants/downloadLimits';
import useHistory from '../../../hooks/useHistory';
import { isPhoneOrTablet } from '../../../utils/utils';
import pubSub from '../../../utils/pubsub';
import { eventCompanyProfileViewed, eventCompanyActionClicked } from '../../../ga4/ga4';
import GeneralError from '../../errors/GeneralError';
import GeneralLoader from '../../loaders/GeneralLoader';
import ShareholderStructureTab from './ShareholderStructure/ShareholderStructureTab';
import ShareholderPopup from './ShareholderStructure/ShareholderPopup';
import { DOWNLOAD_VERDICT } from './ShareholderStructure/graph/graphConstants';
import { getNodeDetailPath } from './ShareholderStructure/shareholderGraphUtils';
import { canNavigateToNodeDetails } from './ShareholderStructure/graph/graphCardModel';
import {
  buildProfilerDetailsBody,
  buildShareholderGraphBody,
  initialShareholderSources,
  isShareholderGraphPending,
  onDetailsShareholdersLoaded,
  onDetailsShareholdersSettled,
  onShareholderGraphAccessDenied,
  onShareholderGraphFailed,
  onShareholderGraphLoaded,
  resetShareholderSources,
  settleShareholderGraphRequest,
  shouldRequestShareholderGraph,
  startShareholderGraphRequest
} from './utils/shareholderGraphLoading';
import { resolveCompanyLocation } from './utils/companyLocation';
import { isTabUnavailable } from './utils/tabAccess';
import { exportBlockedFor } from '../../common/exportAccessTooltips';
import { isNavigableCrefoId } from '../../../utils/idUtils';
import { applyRegisterStatus } from '../../../utils/companyStatus';
import ComparableCompaniesTab from './ComparableCompanies/ComparableCompaniesTab';
import FinancialsTab from './Financials/FinancialsTab';
import KeyContactsTab from './KeyContacts/KeyContactsTab';
import { getKeyContactDetailPath } from './KeyContacts/keyContactsUtils';
import useSimilarCompaniesState from './hooks/useSimilarCompaniesState.jsx';
import useShareholderState, { SHAREHOLDER_GRAPH_STATUS } from './hooks/useShareholderState';
import useFinancialsTabState from './hooks/useFinancialsTabState';
import useDownloadState from './hooks/useDownloadState';
import useEnrichmentAccess from '../../../hooks/useEnrichmentAccess';
import { shouldOpenEnrichmentDownloadDialog } from './utils/enrichmentContactsDownload';
import { getUsableInitialDetails } from './utils/initialDetails';
import CompanyContactsDownloadDialog from './Header/CompanyContactsDownloadDialog';
import useClickOutsideHandlers from './hooks/useClickOutsideHandlers';
import useSubscriptionStatus from '../../../hooks/useSubscriptionStatus';
import useBulkExportConfirm from '../../../hooks/useBulkExportConfirm';
import BulkExportConfirmModal from '../../common/BulkExportConfirmModal';
import ProductPortfolioTab from './ProductPortfolio/ProductPortfolioTab';
import BanksTab from './Banks/BanksTab';
import UpdatesTab from './Updates/UpdatesTab';
import GeneralTab from './GeneralTab/GeneralTab';
import WatchlistRemovePopup from './Header/WatchlistRemovePopup';
import HeaderActionButtons from './Header/HeaderActionButtons';
import HeaderDesktop from './Header/HeaderDesktop';
import HeaderMobile from './Header/HeaderMobile';
import PreHeaderDesktop from './Header/PreHeaderDesktop';
import CompanyDetailsTabs from './CompanyDetailsTabs';
import { buildCompanyDetailsTabs } from './utils/tabsConfig';
import { buildComparablesLoaderCriteria } from './utils/comparablesLoaderCriteria';
import { HEADER_MESSAGE_TYPES } from '../../../utils/constants/header';
import { PUNCTUATION } from '../../../utils/constants/notation';
import { OPTION_KEYS } from '../../../utils/constants/optionKeys';
import { resolveIndustryCode } from '../../../utils/profiler/resolveIndustryCode';
import comparableCompaniesSortOptions from './constants/comparableCompaniesSortOptions';
import { COMPANY_DETAILS_VIEWS_LIST } from './constants/companyDetailsConstants';
import useElementHeightCssVar from '../../../hooks/useElementHeightCssvar';
import ProfilerSaveSearchPopup from '../../../pages/search/SearchResults/components/ProfilerSaveSearchPopup';
import { useSearchResultsLoader } from '../../../pages/search/SearchResults/components/ProfilerSearchResultsLoaderNew';

const SHAREHOLDER_GRAPH_ERROR = 'Failed to load the shareholder structure.';

// Longer than the request's own 20 s timeout, so it only settles a graph download meter whose
// makeRequest exited without any callback.
export const GRAPH_METER_CAP_MS = 30_000;

// The shareholder structure is always replaced as a whole, never changed in place. Held by
// reference, a large graph is not turned into nested observables, and the graph preparation reads
// it as is instead of copying it out of MobX on every rebuild.
const LOCAL_STATE_ANNOTATIONS = {
  shareholdersStructure: observable.ref,
  subsidiaries: observable.ref,
  detailsShareholders: observable.ref
};

const sharedState = observable({
  companiesStack: [],
  addCompanyToStack: (companyId = '') => {
    sharedState.companiesStack.push(companyId);
  },
  removeCompanyFromStack: () => {
    if (sharedState.companiesStack.length) {
      sharedState.companiesStack.pop();
    }
  },
  forceResetCompaniesStack: () => {
    sharedState.companiesStack.length = 0;
  }
});

/**
 * CompanyDetails - Orchestration component for company detail view
 *
 * Manages multiple tab views (General, Financials, Shareholders, Banks, Key Contacts,
 * Product Portfolio, Comparable Companies, Updates) with complex state coordination.
 * Supports nested company details navigation for comparable companies drill-down.
 *
 * @param {Object} props
 * @param {Object} props.company - Company data object with id, name, financials, etc.
 * @param {Function} props.displayDetails - Callback to close/change details view
 * @param {Function} [props.goBackToPreviousCompany] - Callback to navigate back to previous company in stack
 * @param {number} [props.companiesStackLength=0] - Length of parent's companies stack for back navigation
 * @param {string} [props.savedTab=null] - Tab to restore on back navigation (overlay mode)
 * @param {string} [props.savedComparablesId=null] - TD-1709: Comparables ID to restore search results on back navigation
 * @param {boolean} [props.isBackNavigation=false] - Whether this is a back navigation (skip reset)
 * @param {Function} [props.clearBackNavigationFlag] - Callback to clear back navigation flag
 * @param {boolean} [props.isFromWatchList=false] - Whether opened from watchlist context
 * @param {Object} [props.style={}] - Optional container styles
 * @param {Object} props.project - Current project context for access control
 * @param {boolean} [props.isFromFeedbackProject=false] - Whether from shared/feedback project
 * @param {boolean} [props.hideBackButton=false] - Whether to hide back navigation
 * @param {boolean} [props.isStandalonePage=false] - True for URL-navigated pages (skip initial history push)
 * @param {string} [props.backButtonLabel=null] - Label for back button (e.g., "Back to John Smith")
 * @param {string} [props.initialTab=null] - Initial tab to display (e.g., 'shareholders')
 * @param {string} [props.initialComparablesId=null] - TD-1816: Comparables ID for caching results on back navigation
 * @param {Object} [props.initialDetails=null] - `{ companyId, data }`: GET_PROFILER_DETAILS response the host
 *   already loaded. Used once on mount instead of requesting it again; later loads (retry, refresh) fetch.
 */
const CompanyDetails = observer(
  ({
    company = {},
    displayDetails = () => {},
    goBackToPreviousCompany = () => {},
    companiesStackLength = 0,
    savedTab = null,
    savedComparablesId = null,
    isBackNavigation = false,
    clearBackNavigationFlag = () => {},
    isFromWatchList = false,
    style = {},
    project,
    isFromFeedbackProject = false,
    hideBackButton = false,
    isStandalonePage = false,
    backButtonLabel = null,
    initialTab = null,
    initialComparablesId = null,
    initialDetails = null
  }) => {
    const {
      makeRequest,
      utilsStore,
      profilerProjectStore,
      profilerAccessStore,
      getToken,
      authStore
    } = useStore();
    const { navigate, location } = useHistory();

    // Ref to allow hook to access parent state
    const stateRef = useRef(null);

    const similarCompaniesStateRef = useRef(null);

    // Navigation for comparable companies
    // Standalone pages use URL navigation; overlay mode uses displayDetails to stay in context
    const navigateToCompany = (companyToOpen) => {
      if (isStandalonePage) {
        // URL-based navigation for standalone pages
        const path = utilsStore.getCompanyDetailsPath(companyToOpen.id, {
          ref: company.name // Back button shows "Back to [current company name]"
        });
        navigate(path);
      } else {
        // Overlay mode - use displayDetails to navigate within overlay context
        // Pass current tab AND comparablesId so they can be restored on back navigation
        const currentTab = stateRef.current?.tab || null;
        // Use loadingComparablesId as fallback if search is in progress but not yet completed
        const currentComparablesId =
          similarCompaniesStateRef.current?.comparablesId ||
          similarCompaniesStateRef.current?.loadingComparablesId ||
          null;
        displayDetails(companyToOpen, currentTab, currentComparablesId);
      }
    };

    // Similar companies state (extracted to hook)
    const similarCompaniesState = useSimilarCompaniesState({
      makeRequest,
      authStore,
      profilerAccessStore,
      profilerProjectStore,
      utilsStore,
      getObservableCompany: () => stateRef.current?.observableCompany,
      getFinancials: () => stateRef.current?.financials,
      getCurrentTab: () => stateRef.current?.tab,
      isRendered: () => stateRef.current?.rendered !== false,
      isFromFeedbackProject,
      project,
      navigateToCompany,
      initialComparablesId, // TD-1816: Pass comparablesId for cache restoration
      isStandalonePage // Enable URL sync for back navigation caching
    });

    similarCompaniesStateRef.current = similarCompaniesState;

    // Shareholder structure state (extracted to hook)
    const shareholderState = useShareholderState({
      getShareholdersStructure: () => stateRef.current?.shareholdersStructure,
      // The place the address line shows (the target card's fallback)
      getTargetLocation: () =>
        resolveCompanyLocation(
          stateRef.current?.observableCompany,
          stateRef.current?.companyDetails
        ),
      utilsStore
    });

    // Raw shareholder rows of the graph view (current company), for the key-contact ownership
    // join when the details response arrives after the graph.
    const shareholderGraphRowsRef = useRef(null);

    // Financials tab state (extracted to hook)
    const financialsState = useFinancialsTabState({
      getFinancials: () => stateRef.current?.financials
    });

    // Bridge: useDownloadState needs onLimitExceeded → reopenForLimitExceeded
    // (defined later by useBulkExportConfirm). Ref breaks the chicken-and-egg.
    const reopenForLimitExceededRef = useRef(null);

    // Read before useDownloadState, which takes the refetch as its post-export
    // reconcile. Also feeds the bulk export confirmation below.
    const {
      downloads,
      period: contractPeriod,
      isLoading: isSubscriptionLoading,
      refetch: refetchSubscription
    } = useSubscriptionStatus();

    // Download state (extracted to hook)
    const downloadState = useDownloadState({
      getToken,
      utilsStore,
      profilerAccessStore,
      companyId: company.id,
      getComparablesId: () => similarCompaniesState.comparablesId,
      isFromFeedbackProject,
      project,
      onLimitExceeded: (downloadType, body) => {
        // Defense in depth: only the comparables export routes through the
        // bulk modal. Returning false lets useDownloadState fall back to its
        // generic error toast for any other download type whose response
        // (against BE contract) carries DOWNLOAD_LIMIT_EXCEEDED.
        if (downloadType !== 'comparable-companies') return false;
        reopenForLimitExceededRef.current?.(downloadType, body);
        return true;
      },
      onExported: () => refetchSubscription?.()
    });

    // TD-2101: enrichment-enabled users get the v3 XLSX dialog for the contacts
    // download; everyone else keeps the legacy immediate download (TD-1908 guard
    // via shouldOpenEnrichmentDownloadDialog).
    const enrichmentAccess = useEnrichmentAccess();
    const [isContactsDownloadOpen, setIsContactsDownloadOpen] = useState(false);

    const handleDownload = useCallback(
      (downloadType) => {
        // `isDemo` is a no-op here today — canExportCompanyContacts is Lite||Full,
        // so a demo user never reaches this download. It is passed anyway because
        // the demo rule belongs to the helper, not to whichever caller happens to
        // be reachable: the BE 403s an enriched contacts export from a demo caller
        // on BOTH export endpoints, so opening this export to demo (the natural
        // sibling of TD-2652) must not silently reintroduce that failure.
        if (
          shouldOpenEnrichmentDownloadDialog(downloadType, enrichmentAccess.enabled, {
            isDemo: profilerAccessStore.isProfilerDemo
          })
        ) {
          setIsContactsDownloadOpen(true);
          return;
        }
        downloadState.onDownloadClick(downloadType);
      },
      [enrichmentAccess.enabled, downloadState, profilerAccessStore]
    );

    // Bulk export confirmation (TD-2125): the comparables download is a
    // multi-company export, so it routes through the same confirmation dialog
    // already used by ProfilerProjectView and SearchResults.
    const {
      bulkExportPendingType,
      bulkExportPrefillData,
      requestBulkExport,
      confirmBulkExport,
      cancelBulkExport,
      reopenForLimitExceeded
    } = useBulkExportConfirm({
      downloads,
      isLoading: isSubscriptionLoading,
      onDownload: (type) => downloadState.onDownloadClick(type)
    });
    reopenForLimitExceededRef.current = reopenForLimitExceeded;

    // TD-1826: Map tab string to DETAILS_POUP_TABS constant
    const tabStringToConstant = (tabString) => {
      const tabMap = {
        shareholders: DETAILS_POUP_TABS.SHAREHOLDER_STRUCTURE,
        contacts: DETAILS_POUP_TABS.KEY_CONTACTS,
        financials: DETAILS_POUP_TABS.FINANCIALS,
        general: DETAILS_POUP_TABS.GENERAL,
        'shareholder-structure': DETAILS_POUP_TABS.SHAREHOLDER_STRUCTURE,
        banks: DETAILS_POUP_TABS.BANKS,
        'key-contacts': DETAILS_POUP_TABS.KEY_CONTACTS,
        'product-portfolio': DETAILS_POUP_TABS.PRODUCT_PORTFOLIO,
        'similar-companies': DETAILS_POUP_TABS.SIMILAR_COMPANIES,
        updates: DETAILS_POUP_TABS.UPDATES
      };
      return tabMap[tabString] || null;
    };

    // TD-1826: Get initial tab from URL param (priority) or localStorage prop
    const getInitialTabValue = () => {
      // Check URL param first (for standalone pages)
      if (isStandalonePage) {
        const urlParams = new URLSearchParams(window.location.search);
        const tabFromUrl = urlParams.get('tab');
        if (tabFromUrl) {
          const mapped = tabStringToConstant(tabFromUrl);
          if (mapped) return mapped;
        }
      }
      // Fall back to localStorage prop
      if (initialTab) {
        const mapped = tabStringToConstant(initialTab);
        if (mapped) return mapped;
      }
      return DETAILS_POUP_TABS.GENERAL;
    };

    const createState = () => ({
      rendered: true,
      unrender: () => (state.rendered = false),
      observableCompany: company,
      setObservableCompany: (newCompany = {}) => {
        runInAction(() => {
          // Reset details load flag if company changed (overlay navigation), and drop the
          // previous company's details: its court, email and location are not this one's
          if (state.observableCompany?.id !== newCompany?.id) {
            state.detailsLoadedFromEffect = false;
            state.companyDetails = {};
            // The previous company's summary must not show until the new details arrive
            state.ownershipSummary = null;
            state.ownershipSummaryCompanyId = null;
          }
          state.observableCompany = newCompany;
        });
      },
      tab: getInitialTabValue(),
      tabId: 'profiler_search_results_details_tab',
      setTab: (tab = DETAILS_POUP_TABS.GENERAL, tabId = '') => {
        if (state.tab !== tab) {
          state.tab = tab;
          state.tabId = tabId;
          state.scrollTabIntoView(tabId);
          // TD-1826: Sync tab state to URL for standalone pages (replaceState to not pollute history)
          if (isStandalonePage) {
            const params = new URLSearchParams(window.location.search);
            if (tab === DETAILS_POUP_TABS.GENERAL) {
              params.delete('tab');
            } else {
              params.set('tab', tab);
            }
            const newUrl = params.toString()
              ? `${window.location.pathname}?${params.toString()}`
              : window.location.pathname;
            window.history.replaceState({}, '', newUrl);
          }
        }
      },
      scrollTabIntoView: (tabId = '', force = false) => {
        const tabDOM = document.getElementById(tabId);
        if (tabDOM) {
          setTimeout(() => {
            tabDOM.scrollIntoView({
              ...(force ? {} : { behavior: 'smooth' }),
              block: 'nearest',
              inline: 'center'
            });
          }, 0);
        }
      },
      now: Date.now(),
      setDateNow: (ms) => (state.now = ms),
      updateTabVisited: false,
      flagUpdateTabVisited: () => {
        state.updateTabVisited = true;
      },
      isAddRemoveWatchlistPopupOpen: false,
      setIsAddRemoveWatchlistPopupOpen: (open = false) => {
        state.isAddRemoveWatchlistPopupOpen = open;
        utilsStore.lockScroll(open);
      },
      componentVariables: {
        lastScrollY: 0,
        isScrollingToShareholderStructure: false
      },
      isMainCompanyInTheStack: sharedState.companiesStack.length === 0,
      // Similar companies state - now accessed directly via similarCompaniesState
      // Proxy getters removed - use similarCompaniesState.xyz or pass to components
      isFinancialsDataEmpty: false,
      isFinancialsConsolidatedDataEmpty: true,
      isFinancialsIndividualDataEmpty: true,
      financials: null,
      shareholdersStructure: null,
      // undefined until the backend sends bank data; drives the Banks tab (see tabsConfig)
      bankConnections: undefined,
      ownershipSummary: null,
      // Company `ownershipSummary` belongs to; render checks it because the reset above runs
      // in an effect, after the first render with the new company prop
      ownershipSummaryCompanyId: null,
      subsidiaries: [],
      companyDetails: {},
      topManagement: null,
      keyContacts: null,
      contactsRoleFilterOptions: [],
      contactsCountryFilterOptions: [],
      isLoadingDetails: true,
      loadDetailsError: null,
      // Company whose details request is in flight, null when none
      detailsRequestCompanyId: null,
      detailsLoadedFromEffect: false,
      isIndependent: false,
      ...initialShareholderSources(),
      isCurrentCompany: (companyId) => state.rendered && state.observableCompany?.id === companyId,
      // Shareholder graph view: requested at page load next to the details, once per company.
      // Only the answer of the request in flight for the shown company counts: older ones
      // (company changed, unmounted, a retry) are dropped; never use cancelRequest here. A retry
      // while a request is in flight joins it. Without access the structure settles as empty, so
      // no loader waits for it.
      loadShareholderGraph: () => {
        const requestedCompanyId = state.observableCompany?.id;
        if (!requestedCompanyId) {
          return;
        }
        if (!profilerAccessStore.canViewProfilerDetails(project)) {
          onShareholderGraphAccessDenied(state, shareholderState);
          return;
        }

        const requestId = startShareholderGraphRequest(state);
        if (requestId === null) {
          return;
        }
        shareholderState.setGraphStatus(SHAREHOLDER_GRAPH_STATUS.LOADING);
        // The request layer calls no callback on an abort or an expired session; the company
        // reset clears the request then.
        const settle = () =>
          settleShareholderGraphRequest(state, requestId) &&
          state.isCurrentCompany(requestedCompanyId);
        makeRequest({
          endpoint: API_ENDPOINTS.GET_PROFILER_SHAREHOLDER_GRAPH,
          body: buildShareholderGraphBody(requestedCompanyId, { isFromFeedbackProject, project }),
          onSuccess: (response) => {
            if (settle()) {
              onShareholderGraphLoaded(state, shareholderState, response, shareholderGraphRowsRef);
            }
          },
          onError: () => {
            if (settle()) {
              onShareholderGraphFailed(state, shareholderState);
            }
          }
        });
      },
      // A details response of the shown company (callers check that). The shareholder structure
      // comes from the graph view: the details only keep theirs as its fallback and give the key
      // contacts the graph's ownership shares when the graph arrived first.
      applyDetailsResponse: (response) => {
        state.financials = { details: response.details, general: response.general };

        // Initialize financials tab state from response
        financialsState.initializeFromResponse(response);

        state.companyDetails = response.companyDetails;
        state.topManagement = response.topManagement;
        onDetailsShareholdersLoaded(state, shareholderState, response, shareholderGraphRowsRef);
        state.keyContacts = response.keyContacts;
        state.contactsRoleFilterOptions = response.contactsRoleFilterOptions;
        state.contactsCountryFilterOptions = response.contactsCountryFilterOptions;
        state.isFinancialsDataEmpty = response.isFinancialsDataEmpty;
        state.isFinancialsConsolidatedDataEmpty = response.isFinancialsConsolidatedDataEmpty;
        state.isFinancialsIndividualDataEmpty = response.isFinancialsIndividualDataEmpty;
        state.bankConnections = response.bankConnections;
        state.ownershipSummary = response.ownershipSummary ?? null;
        state.ownershipSummaryCompanyId = state.observableCompany?.id ?? null;
        state.leaveUnavailableTab();
      },
      // A link to a tab with nothing to show lands on General: the shareholder structure, key
      // contacts or banks when the user may not see details, Banks when the company has none
      leaveUnavailableTab: () => {
        if (
          isTabUnavailable(state.tab, {
            canViewDetails: profilerAccessStore.canViewProfilerDetails(project),
            bankConnections: state.bankConnections
          })
        ) {
          state.setTab(DETAILS_POUP_TABS.GENERAL, 'profiler_search_results_details_tab');
        }
      },
      // Details the host already loaded (see utils/initialDetails). Returns false when
      // they cannot be applied, so the caller loads them instead.
      applyInitialDetails: (response) => {
        try {
          state.applyDetailsResponse(response);
        } catch (error) {
          console.error(
            'ERROR - applyInitialDetails - Could not apply initial details, loading them:',
            error
          );
          return false;
        }
        state.loadDetailsError = null;
        state.isLoadingDetails = false;
        // Settled like a loaded response, so a failed graph view can still fall back to them
        onDetailsShareholdersSettled(state, shareholderState);
        return true;
      },
      loadDetails: () => {
        const companyId = state.observableCompany.id;
        if (
          state.detailsRequestCompanyId === companyId ||
          !profilerAccessStore.canViewProfilerDetails(project)
        ) {
          if (!profilerAccessStore.canViewProfilerDetails(project)) {
            state.isLoadingDetails = false;
            state.leaveUnavailableTab();
          }
          return;
        }

        state.detailsRequestCompanyId = companyId;
        state.isLoadingDetails = true;
        state.loadDetailsError = null;
        // Overlay hosts swap the company on this instance: drop a response that
        // arrives after the user has moved to another company
        const isForShownCompany = () => state.isCurrentCompany(companyId);
        makeRequest({
          endpoint: API_ENDPOINTS.GET_PROFILER_DETAILS,
          body: buildProfilerDetailsBody(companyId, { isFromFeedbackProject, project }),
          onSuccess: (response) => {
            if (!isForShownCompany()) {
              return;
            }

            state.applyDetailsResponse(response);
          },
          onError: (errorMessage) => {
            if (!isForShownCompany()) {
              return;
            }

            state.loadDetailsError = errorMessage || 'Failed to load company details.';
          },
          onFinally: () => {
            if (!state.rendered) {
              return;
            }

            if (state.detailsRequestCompanyId === companyId) {
              state.detailsRequestCompanyId = null;
            }
            if (isForShownCompany()) {
              state.isLoadingDetails = false;
              onDetailsShareholdersSettled(state, shareholderState);
            }
          }
        });
      },
      // Financials tab state - now accessed via financialsState
      // Download state - now accessed via downloadState
      get layout() {
        return utilsStore.windowWidth < 1201 ? 'mobile' : 'desktop';
      },
      isLoadingSupplementaryData: false,
      loadSupplementaryDataError: null,
      loadSupplementaryData: () => {
        if (state.isLoadingSupplementaryData) {
          return;
        }

        state.isLoadingSupplementaryData = true;
        makeRequest({
          endpoint: API_ENDPOINTS.GET_PROFILER_COMPANY_SUPPLEMENTARY_DATA,
          body: { companyId: company.id, searchId: project.searchId },
          onSuccess: ({ companyData }) => {
            const fieldsToOverride = [
              'description',
              'foundingYear',
              'ownership',
              'productPortfolio',
              'productPortfolioDescription',
              'keyProducts',
              'businessVerticals',
              'keyBrands',
              'ownershipMatch',
              'companyStatus',
              'companyStatusSince',
              'companyStatusSincePrecision'
            ];
            fieldsToOverride.forEach((key) => {
              company[key] = companyData[key];
            });
            // The row's filter value and sort ranks follow the status now shown on it. Whether
            // the list offers that filter and those sorts still depends on the list read only.
            applyRegisterStatus(company);
          },
          onError: (errorMessage) => {
            state.loadSupplementaryDataError =
              errorMessage || 'Failed to load company supplementary data';
            utilsStore.setHeaderMessage(
              state.loadSupplementaryDataError,
              HEADER_MESSAGE_TYPES.ERROR
            );
          },
          onFinally: () => {
            state.isLoadingSupplementaryData = false;
          }
        });
      }
    });
    const state = useLocalObservable(createState, LOCAL_STATE_ANNOTATIONS);

    // Set ref so hook can access parent state
    stateRef.current = state;

    // Click-outside handlers (extracted to hook)
    useClickOutsideHandlers({
      financialsState,
      similarCompaniesState
    });

    useElementHeightCssVar(
      '.tabs-wrapper.sticky-similar-companies',
      '--company-details-tabs-height',
      false // No modal pattern - height doesn't change dynamically
    );

    useEffect(() => {
      authStore.loadCompanyUsers();
    }, [authStore]);

    useElementHeightCssVar(
      '.similar-companies-wrap > .similar-listing-layout > .results > .results-header-wrap',
      '--company-details-similar-results-header-height-desktop'
    );
    useElementHeightCssVar(
      '.similar-companies-wrap > .similar-listing-layout > .results > .results-header-wrap-mobile',
      '--company-details-similar-results-header-height-mobile'
    );

    // Effects moved to self-contained tabs: see ComparableCompaniesTab, KeyContactsTab,
    // FinancialsTab, UpdatesTab for tab-specific effects that were previously here.

    // Cleanup: close shareholder popup on unmount
    useEffect(() => {
      return () => {
        if (shareholderState.toggledShareholderPopup) {
          shareholderState.toggleShareholderPopup();
        }
      };
    }, [shareholderState]);

    useEffect(() => {
      return () => {
        if (similarCompaniesState.saveProfilerComparablesSearchPopupDisplayed) {
          similarCompaniesState.toggleProfilerComparablesSaveProjectPopup();
        }
      };
    }, [similarCompaniesState]);

    useEffect(() => {
      state.setObservableCompany(company);
    }, [state, company]);

    // Analytics: company profile viewed (fires once per company; standalone, overlay, nested nav).
    useEffect(() => {
      if (!company?.id) return;
      const params = new URLSearchParams(window.location.search);
      const refParam = (params.get('ref') || '').toLowerCase();
      let refSource = 'direct';
      if (refParam.includes('search')) refSource = 'search results';
      else if (isFromWatchList) refSource = 'watchlist';
      else if (isFromFeedbackProject) refSource = 'project';
      else if (refParam) refSource = 'related_entity';
      // The `ref` label may be a company/person name (PII) — never push it raw. searchId is a UUID.
      eventCompanyProfileViewed(company.id, refSource, params.get('searchId') || null);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [company?.id]);

    // Reset state when company changes (overlay navigation)
    // On forward nav: reset tab to General, clear comparables
    // On back nav: restore saved tab, load cached comparables
    const prevCompanyIdRef = useRef(company?.id);
    const clearBackNavTimeoutRef = useRef(null);
    useEffect(() => {
      // Clear any pending timeout from previous navigation to prevent stale clears
      if (clearBackNavTimeoutRef.current) {
        clearTimeout(clearBackNavTimeoutRef.current);
        clearBackNavTimeoutRef.current = null;
      }

      if (prevCompanyIdRef.current && company?.id && prevCompanyIdRef.current !== company.id) {
        if (isBackNavigation) {
          // Back navigation: restore saved tab, load cached comparables
          if (savedTab) {
            state.setTab(savedTab, 'profiler_search_results_details_tab');
          }

          // Cancel any pending load from the previous company before loading cache
          // This is necessary because the suspend flag from the previous company's load
          // would block the cache load for the current company
          clearInterval(similarCompaniesState.comparablesPollInterval);
          similarCompaniesState.isSimilarCompaniesLoadSuspend = false;

          if (savedComparablesId) {
            // Load comparables from cache (will fall back to fresh search on cache miss)
            similarCompaniesState.loadCachedSimilarCompanies(savedComparablesId);
          } else {
            // No cached ID available - prevent fresh search by suppressing initial load
            // This handles cases where user navigated away before search completed
            similarCompaniesState.suppressInitialLoad();
          }
          // Delay clearing the flag until after all effects have run in this tick.
          // This prevents the initial load effect from re-running with isBackNavigation=false
          // before the isSimilarCompaniesLoadSuspend flag is properly observed.
          clearBackNavTimeoutRef.current = setTimeout(() => {
            clearBackNavigationFlag();
            clearBackNavTimeoutRef.current = null;
          }, 0);
        } else {
          // Forward navigation: reset everything
          similarCompaniesState.resetForNewCompany();
          state.setTab(DETAILS_POUP_TABS.GENERAL, 'profiler_search_results_details_tab');
        }
        window.scrollTo({ top: 0 });
      }
      prevCompanyIdRef.current = company?.id;

      // Cleanup: clear timeout on unmount or before next effect run
      return () => {
        if (clearBackNavTimeoutRef.current) {
          clearTimeout(clearBackNavTimeoutRef.current);
          clearBackNavTimeoutRef.current = null;
        }
      };
    }, [
      company?.id,
      similarCompaniesState,
      state,
      isBackNavigation,
      savedTab,
      savedComparablesId,
      clearBackNavigationFlag
    ]);

    useEffect(() => {
      if (company.retrieveSupplementaryData) {
        state.loadSupplementaryData();
      }
    }, [state, company]);

    // Trigger initial similar companies load on mount (moved here from ComparableCompaniesTab)
    // This must run regardless of which tab is active so the tab becomes enabled
    useEffect(() => {
      if (
        state.observableCompany?.revenue &&
        similarCompaniesState.similarCompaniesInitialLoad &&
        !similarCompaniesState.isLoadingSimilarCompanies &&
        !similarCompaniesState.isSimilarCompaniesLoadSuspend && // Prevent race with cache load on back nav
        !isBackNavigation // Skip on back navigation - back nav effect will handle state
      ) {
        similarCompaniesState.loadSimilarCompanies();
      }
    }, [state.observableCompany, similarCompaniesState, isBackNavigation]);

    // Shareholder graph state belongs to one company. Overlay mode reuses this instance across
    // companies, so it is reset on every company change. Declared before the details effect
    // (effects run in declaration order), so a new company's details, loaded or handed over by
    // the host, never meet old graph state.
    const observableCompanyId = state.observableCompany?.id;
    useEffect(() => {
      shareholderState.resetForCompany();
      resetShareholderSources(state);
      shareholderGraphRowsRef.current = null;
    }, [shareholderState, state, observableCompanyId]);

    // Graph view at page load, in parallel with the details (the General tab needs the filtered
    // ultimate list). Once per company: the reset above sets the status back to idle. Waits until
    // access is final: a Viewer's access depends on the project owner in authStore.companyUsers.
    // Independent of the details effect below, so it also runs when the host hands details over.
    const canViewShareholderDetails = profilerAccessStore.canViewProfilerDetails(project);
    const companyUsersLoaded = !!authStore.companyUsers?.length;
    useEffect(() => {
      if (
        shouldRequestShareholderGraph({
          status: shareholderState.graphStatus,
          canViewDetails: canViewShareholderDetails,
          companyUsersLoaded
        })
      ) {
        state.loadShareholderGraph();
      }
    }, [
      shareholderState,
      state,
      observableCompanyId,
      shareholderState.graphStatus,
      canViewShareholderDetails,
      companyUsersLoaded
    ]);

    // Captured once: only the first details load of this mount may use the handoff
    const initialDetailsRef = useRef(initialDetails);

    useEffect(() => {
      if (
        state.observableCompany &&
        authStore.companyUsers?.length &&
        !state.detailsLoadedFromEffect
      ) {
        runInAction(() => {
          state.detailsLoadedFromEffect = true;
        });
        const usableInitialDetails = getUsableInitialDetails(initialDetailsRef.current, {
          companyId: state.observableCompany.id,
          canViewDetails: profilerAccessStore.canViewProfilerDetails(project),
          isFromFeedbackProject
        });
        initialDetailsRef.current = null;
        if (!usableInitialDetails || !state.applyInitialDetails(usableInitialDetails)) {
          state.loadDetails();
        }
      }
    }, [
      state,
      state.observableCompany,
      authStore.companyUsers,
      profilerAccessStore,
      project,
      isFromFeedbackProject
    ]);

    useEffect(() => {
      const interval = setInterval(() => state.setDateNow(Date.now()), 60_000);
      return () => {
        state.unrender();
        clearInterval(interval);
        if (state.isAddRemoveWatchlistPopupOpen) {
          state.setIsAddRemoveWatchlistPopupOpen();
        }
      };
    }, [state]);

    // Track company in navigation stack - runs once on mount/unmount only
    // company.id is intentionally not in deps as we only want initial registration
    useEffect(() => {
      sharedState.addCompanyToStack(company.id);
      return () => {
        sharedState.removeCompanyFromStack();
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Listen for force-close events from external sources (e.g., navigation changes)
    useEffect(() => {
      if (state.isMainCompanyInTheStack) {
        const listenForForceClose = (payload) => {
          if (payload?.event === 'force-close-company-details') {
            if (sharedState.companiesStack.length === 1) {
              navigate(-1);
            } else {
              navigate(-sharedState.companiesStack.length);
              displayDetails(null);
              sharedState.forceResetCompaniesStack();
            }
          }
        };
        pubSub.subscribe(listenForForceClose);
        return () => {
          pubSub.unsubscribe(listenForForceClose);
        };
      }
    }, [state.isMainCompanyInTheStack, navigate, displayDetails]);

    // Handle browser back button for modal pattern (non-URL navigation)
    // For standalone URL-navigated pages, browser handles navigation natively
    // Uses stack-based navigation for proper back button behavior in overlay mode
    useEffect(() => {
      if (isStandalonePage) return; // URL navigation - browser handles back natively

      // Modal pattern: push history entry so back button closes the modal
      navigate(location.pathname + location.search);

      const handlePopstate = () => {
        if (companiesStackLength > 1) {
          goBackToPreviousCompany(); // Pop parent stack, show previous company
        } else {
          displayDetails(null); // Close overlay
        }
      };

      addEventListener('popstate', handlePopstate);
      return () => removeEventListener('popstate', handlePopstate);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isStandalonePage, companiesStackLength, goBackToPreviousCompany, displayDetails]);

    useEffect(() => {
      return () => {
        if (state.updateTabVisited) {
          profilerProjectStore.updateWatchlistCompanyNewsSeenDate(company.id);
        }
      };
    }, [state, profilerProjectStore, company.id]);

    // Track scroll position
    useEffect(() => {
      const onScroll = () => {
        const st = window.pageYOffset || document.documentElement.scrollTop;
        runInAction(() => {
          state.componentVariables.lastScrollY = st <= 0 ? 0 : st;
        });
      };

      window.addEventListener('scroll', onScroll);
      return () => window.removeEventListener('scroll', onScroll);
    }, [state]);

    const tabs = buildCompanyDetailsTabs({
      state,
      similarCompaniesState,
      profilerAccessStore,
      project,
      company,
      isFromWatchList,
      isShareholderGraphLoading: isShareholderGraphPending(shareholderState.graphStatus)
    });

    // Context query params preserved when building per-tab URLs so Cmd/Ctrl/middle-click
    // opens a new tab that retains back-ref and search context. Only relevant in
    // standalone mode; in overlay mode tabs are not rendered as anchors.
    const tabsUrlContext = useMemo(() => {
      if (!isStandalonePage) return {};
      const params = new URLSearchParams(location.search);
      const context = {};
      const ref = params.get('ref');
      const searchKey = params.get('searchKey');
      const searchId = params.get('searchId');
      const comparablesId = params.get('comparablesId');
      if (ref) context.ref = ref;
      if (searchKey) context.searchKey = searchKey;
      if (searchId) context.searchId = searchId;
      if (comparablesId) context.comparablesId = comparablesId;
      return context;
    }, [isStandalonePage, location.search]);

    const isInWatchlist =
      profilerProjectStore.watchlist.findIndex((c) => c.id === company.id) !== -1;

    const watchlistButtonTooltipContent = !profilerAccessStore.canViewProfilerWatchlist ? (
      <>
        Requires Profiler Lite or Profiler Full access.
        <br />
        Contact <a href="mailto:support@transact.digital">support@transact.digital</a> for
        assistance.
      </>
    ) : isInWatchlist ? (
      'Remove from watchlist'
    ) : (
      'Add to watchlist'
    );
    const watchlistButtonTooltipPosition =
      !profilerAccessStore.canViewProfilerWatchlist &&
      utilsStore.windowWidth > 450 &&
      utilsStore.windowWidth <= 1200
        ? 'bottom-end'
        : 'bottom-center';
    const watchlistButtonTooltipClickable = !profilerAccessStore.canViewProfilerWatchlist;
    const companyLinkedInButtonTooltipText = 'LinkedIn';

    const onWatchlistButtonClick = useCallback(() => {
      eventCompanyActionClicked(company.id, 'watchlist_toggle');
      if (isInWatchlist) {
        state.setIsAddRemoveWatchlistPopupOpen(true);
      } else {
        profilerProjectStore.addToWatchlist(company);
      }
    }, [isInWatchlist, state, profilerProjectStore, company]);

    const onWebsiteActionClick = () => {
      eventCompanyActionClicked(company.id, 'website_visit');
      window.open('https://' + company.website_SAFE, '_blank');
    };
    const onLinkedInActionClick = () => {
      eventCompanyActionClicked(company.id, 'linkedin_visit');
      window.open(
        `https://www.linkedin.com/search/results/companies/?keywords=${encodeURIComponent(
          company.name
        )}`,
        '_blank'
      );
    };
    const onSearchActionClick = () => {
      eventCompanyActionClicked(company.id, 'search_similar');
      utilsStore.openProfilerSearchCriteriaAndFillFromCompanyDetails(
        state.observableCompany,
        state.financials.details.consolidated,
        state.financials.details.individual
      );
    };
    const onDownloadToggleClick = () => {
      eventCompanyActionClicked(company.id, 'download');
      financialsState.toggleFinancialDownload();
    };

    // Navigate to subsidiary (company) details in same tab. Like the graph cards, the live
    // profile uuid (company_uuid) is preferred; the numeric crefo member_id is the fallback.
    const handleViewSubsidiaryDetails = useCallback(
      (subsidiary) => {
        const memberId = subsidiary?.member_id;
        const companyUuid = subsidiary?.company_uuid || null;

        if (!companyUuid && !isNavigableCrefoId(memberId)) {
          console.warn(
            '[CompanyDetails] Cannot navigate - subsidiary missing company_uuid and numeric member_id:',
            subsidiary?.member_name,
            { member_id: memberId }
          );
          return;
        }

        // Back button shows "Back to [company name]"
        navigate(getNodeDetailPath({ member_id: memberId }, company.name, utilsStore, companyUuid));
      },
      [navigate, company.name, utilsStore]
    );

    // Navigate to shareholder (person, company or subsidiary) details in same tab. Persons open by
    // their numeric member_id (crefo_id); companies and subsidiaries take the subsidiary path
    // (company_uuid first), as the shared navigability gate allows both.
    const handleViewShareholderDetails = useCallback(
      (shareholder) => {
        if (shareholder?.type === 'company' || shareholder?.type === 'subsidiary') {
          handleViewSubsidiaryDetails(shareholder);
          return;
        }

        const memberId = shareholder?.member_id;

        if (!isNavigableCrefoId(memberId)) {
          console.warn(
            '[CompanyDetails] Cannot navigate - shareholder missing numeric member_id:',
            shareholder?.member_name,
            { type: shareholder?.type, member_id: memberId }
          );
          return;
        }

        if (shareholder?.type === 'person') {
          const path = utilsStore.getIndividualDetailsPath(memberId, {
            ref: company.name // Back button shows "Back to [company name]"
          });
          navigate(path);
        }
      },
      [handleViewSubsidiaryDetails, navigate, company.name, utilsStore]
    );

    // Individual profile path of a key contact, shared by same-tab and new-tab opening
    const getKeyContactDetailsPath = useCallback(
      (contact) => {
        const path = getKeyContactDetailPath(contact, company.name, utilsStore);

        if (!path) {
          console.warn(
            '[CompanyDetails] Cannot navigate - contact missing numeric person_crefo_id:',
            contact?.first_name,
            contact?.last_name,
            { person_crefo_id: contact?.person_crefo_id }
          );
        }

        return path;
      },
      [company.name, utilsStore]
    );

    // Navigate to key contact (person) details in same tab
    const handleViewKeyContactDetails = useCallback(
      (contact) => {
        const path = getKeyContactDetailsPath(contact);
        if (path) navigate(path);
      },
      [navigate, getKeyContactDetailsPath]
    );

    // Open key contact (person) details in a new browser tab
    const handleOpenKeyContactInNewTab = useCallback(
      (contact) => utilsStore.openInNewTab(getKeyContactDetailsPath(contact)),
      [utilsStore, getKeyContactDetailsPath]
    );

    // Shareholder graph nodes (raw = the node's legacy row, node = the prepared graph node).
    // Company pages open by the live profile uuid when known (card.linkId), else the crefo id.
    // The Ultimate shareholder(s) and Subsidiaries tables pass their row only: the row's own
    // company_uuid then comes first, as in the tables' same-tab navigation.
    const getShareholderNodeHref = useCallback(
      (raw, node) =>
        getNodeDetailPath(
          raw,
          company.name,
          utilsStore,
          node ? node.card?.linkId : raw?.company_uuid
        ),
      [company.name, utilsStore]
    );

    const handleViewNodeDetails = useCallback(
      (raw, node) => {
        if (!node?.card?.navigable) return;
        if (shareholderState.toggledShareholderPopup) {
          shareholderState.toggleShareholderPopup();
        }
        navigate(getShareholderNodeHref(raw, node));
      },
      [getShareholderNodeHref, navigate, shareholderState]
    );

    // Open shareholder graph node details in a new browser tab. A table row (no prepared node)
    // is checked with canNavigateToNodeDetails, the gate of the cards and the tables' icons.
    const handleOpenNodeInNewTab = useCallback(
      (raw, node) => {
        const navigable = node ? node.card?.navigable : canNavigateToNodeDetails(raw);
        if (!navigable) return;
        window.open(getShareholderNodeHref(raw, node), '_blank');
      },
      [getShareholderNodeHref]
    );

    const handleShareholderExportError = useCallback(
      (message) => utilsStore.setHeaderMessage(message, HEADER_MESSAGE_TYPES.ERROR),
      [utilsStore]
    );

    // The graph PNG is drawn in the browser, so the download meter is asked once the image
    // exists, as the page's other exports record theirs in the backend. makeRequest can exit
    // without any callback (no token: it shows the session prompt instead; a logout abort), so
    // the cap settles the export as failed rather than leaving Download busy. The counter is
    // reconciled when the balance moved or is used up, as onExported does.
    const handleConfirmGraphDownload = useCallback(
      () =>
        new Promise((resolve) => {
          const cap = setTimeout(() => resolve(DOWNLOAD_VERDICT.FAILED), GRAPH_METER_CAP_MS);
          const settle = (verdict) => {
            clearTimeout(cap);
            resolve(verdict);
          };
          Promise.resolve(
            makeRequest({
              endpoint: API_ENDPOINTS.EXPORT_PROFILER_SHAREHOLDER_GRAPH,
              body: { companyId: company.id },
              onSuccess: (payload) => {
                if (payload?.recorded > 0) refetchSubscription?.();
                settle(DOWNLOAD_VERDICT.RECORDED);
              },
              onError: (_message, response) => {
                const isLimit = response?.code === DOWNLOAD_LIMIT_EXCEEDED_CODE;
                if (isLimit) refetchSubscription?.();
                settle(isLimit ? DOWNLOAD_VERDICT.LIMIT : DOWNLOAD_VERDICT.FAILED);
              }
            })
          ).catch(() => settle(DOWNLOAD_VERDICT.FAILED));
        }),
      [company.id, makeRequest, refetchSubscription]
    );

    const { companyAddress, companyGoogleMapsLink } = useMemo(() => {
      const companyStreet = state.observableCompany.street || state.companyDetails?.street;
      const companyPostCode = state.observableCompany.postcode || state.companyDetails?.postcode;
      const { city: companyCity, country: companyCountry } = resolveCompanyLocation(
        state.observableCompany,
        state.companyDetails
      );

      const rawAddressParts = [
        companyStreet,
        [companyPostCode, companyCity].filter(Boolean).join(' ').trim(),
        companyCountry
      ];

      const visibleAddressParts = rawAddressParts.filter(Boolean);
      const address =
        visibleAddressParts.length > 0 ? (
          <>{visibleAddressParts.join(', ')}</>
        ) : (
          <>{PUNCTUATION.EMPTY_VALUE}</>
        );

      const googleMapsParts = [companyStreet, companyCity, companyCountry, companyPostCode].filter(
        Boolean
      );
      const mapsLink =
        googleMapsParts.length > 0
          ? `https://www.google.com/maps/place/${googleMapsParts.map(encodeURIComponent).join('+')}`
          : null;

      return { companyAddress: address, companyGoogleMapsLink: mapsLink };
    }, [state.observableCompany, state.companyDetails]);

    const showSimilarCompaniesLoader = similarCompaniesState.isLoadingSimilarCompanies;

    const isShareholderGraphLoading = isShareholderGraphPending(shareholderState.graphStatus);
    const isShareholderGraphFailed =
      shareholderState.graphStatus === SHAREHOLDER_GRAPH_STATUS.FAILED;

    // Graph props shared by the tab and the full-screen popup. Download follows the page's
    // other exports: the one-pager rule (Lite or Full, and not export-disabled) and the
    // download meter (handleConfirmGraphDownload).
    const shareholderGraphProps = {
      getNodeHref: getShareholderNodeHref,
      onViewNodeDetails: handleViewNodeDetails,
      onOpenNodeInNewTab: handleOpenNodeInNewTab,
      onToggleNodeCollapsed: shareholderState.toggleNodeCollapsed,
      onShowAllFiltered: shareholderState.showAllFiltered,
      isLayoutLocked: shareholderState.isLayoutLocked,
      onToggleLayoutLock: shareholderState.toggleLayoutLock,
      canExport: !exportBlockedFor(profilerAccessStore.isProfilerExportDisabled)(
        profilerAccessStore.canExportProfilerOnepager
      ),
      isExportDisabled: profilerAccessStore.isProfilerExportDisabled,
      onExportError: handleShareholderExportError,
      onConfirmDownload: handleConfirmGraphDownload
    };

    // Shared download popup props for both mobile and desktop headers
    // Note: Not using useMemo here because MobX observables need to be accessed
    // during render for observer to track them properly
    const downloadPopupProps = {
      canExportOnepager: profilerAccessStore.canExportProfilerOnepager,
      canExportFinancials: profilerAccessStore.canExportProfilerFinancials,
      canExportFullProfile: profilerAccessStore.canExportProfilerFullProfiler,
      canExportComparables: profilerAccessStore.canExportProfilerComparableCompanies,
      canExportContacts: profilerAccessStore.canExportCompanyContacts,
      isExportDisabled: profilerAccessStore.isProfilerExportDisabled,
      isDownloadingOnepager: downloadState.isDownloadingCompanyOnepager,
      isDownloadingFinancials: downloadState.isDownloadingCompanyFinancials,
      isDownloadingFullProfile: downloadState.isDownloadingCompanyFullProfile,
      isDownloadingComparables: downloadState.isDownloadingCompanyComparables,
      isDownloadingContacts: downloadState.isDownloadingCompanyContacts,
      isSuccessOnepager: downloadState.isSuccessDownloadCompanyOnepager,
      isSuccessFinancials: downloadState.isSuccessDownloadCompanyFinancials,
      isSuccessFullProfile: downloadState.isSuccessDownloadCompanyFullProfile,
      isSuccessComparables: downloadState.isSuccessDownloadCompanyComparables,
      isSuccessContacts: downloadState.isSuccessDownloadCompanyContacts,
      hasContacts: !!state.keyContacts?.length,
      isFinancialsDataEmpty: state.isFinancialsDataEmpty,
      isLoadingDetails: state.isLoadingDetails,
      hasRevenue: !!company.revenue,
      similarCompaniesInitialLoad: similarCompaniesState.similarCompaniesInitialLoad,
      isLoadingSimilarCompanies: similarCompaniesState.isLoadingSimilarCompanies,
      loadSimilarCompaniesError: similarCompaniesState.loadSimilarCompaniesError,
      hasSimilarCompanies: !!similarCompaniesState.similarCompanies.length
    };

    const comparablesSearchLoader = useSearchResultsLoader({
      searchId: similarCompaniesState.loadingComparablesId,
      withRationale: true,
      isComparables: true,
      start: showSimilarCompaniesLoader && similarCompaniesState.loadingComparablesId,
      criteria: buildComparablesLoaderCriteria(
        similarCompaniesState.comparablesLoaderCriteriaFields
      )
    });

    const mobileHeaderActionButtons = (
      <HeaderActionButtons
        company={company}
        isToggleFinancialDownload={financialsState.toggledFinancialDownload}
        linkedInTooltipText={companyLinkedInButtonTooltipText}
        searchTooltipText={
          profilerAccessStore.canViewProfilerDetailsExtendedData(project) && state.isLoadingDetails
            ? 'Loading financial data'
            : 'Initiate search based on this company profile'
        }
        isSearchDisabled={
          profilerAccessStore.canViewProfilerDetailsExtendedData(project) && state.isLoadingDetails
        }
        watchlistTooltipContent={watchlistButtonTooltipContent}
        watchlistTooltipPosition={watchlistButtonTooltipPosition}
        watchlistTooltipClickable={watchlistButtonTooltipClickable}
        isWatchlistDisabled={
          profilerProjectStore.isLoadingWatchlist ||
          profilerProjectStore.initialLoadWatchlist ||
          !!profilerProjectStore.companiesInWatchlistAction[company.id] ||
          !profilerAccessStore.canViewProfilerWatchlist
        }
        isInWatchlist={isInWatchlist}
        downloadTooltipContent={<>Download as</>}
        windowWidth={utilsStore.windowWidth}
        downloadPopupProps={downloadPopupProps}
        onWebsiteClick={onWebsiteActionClick}
        onLinkedInClick={onLinkedInActionClick}
        onSearchClick={onSearchActionClick}
        onWatchlistClick={onWatchlistButtonClick}
        onDownloadToggle={onDownloadToggleClick}
        onDownload={handleDownload}
        requestBulkExport={requestBulkExport}
      />
    );

    // Comparable companies now use URL navigation (no modal rendering)

    return (
      <>
        <BulkExportConfirmModal
          isOpen={bulkExportPendingType !== null}
          totalCompanies={
            bulkExportPendingType === 'comparable-companies'
              ? (similarCompaniesState.similarCompanies?.length ?? 0)
              : 1
          }
          downloads={downloads}
          period={contractPeriod}
          source="comparables"
          sourceId={similarCompaniesState.comparablesId}
          prefillData={bulkExportPrefillData}
          onConfirm={confirmBulkExport}
          onCancel={cancelBulkExport}
        />
        <ShareholderPopup
          isOpen={shareholderState.toggledShareholderPopup}
          graph={shareholderState.graph}
          figuresStatus={shareholderState.figuresStatus}
          companyName={company.name}
          percentageFilter={shareholderState.appliedGraphFilters.percentageFilter}
          filters={shareholderState.popupShareholderStructureFilters}
          validationMessages={shareholderState.validationFieldsPopupShareholderStructure.messages}
          showOnSubmitErrorState={shareholderState.onSubmitErrorStatePopupShareholderStructure}
          isFilterDisabled={shareholderState.isPopupShareholderStructureFilterDisabled}
          isClearDisabled={shareholderState.isClearFiltersDisabled}
          onClose={shareholderState.toggleShareholderPopup}
          onFilterChange={shareholderState.setPopupShareholderStructureFiltersValue}
          onApply={shareholderState.onPopupShareholderStructureFilter}
          onClear={shareholderState.onPopupShareholderStructureClearFilter}
          {...shareholderGraphProps}
        />
        {similarCompaniesState.saveProfilerComparablesSearchPopupDisplayed && (
          <ProfilerSaveSearchPopup
            toggleSaveProjectPopup={similarCompaniesState.toggleProfilerComparablesSaveProjectPopup}
            onSaveSearch={similarCompaniesState.onProfilerComparablesSaveSearch}
            projectId={similarCompaniesState.comparablesProjectId}
          />
        )}
        {state.isAddRemoveWatchlistPopupOpen && (
          <WatchlistRemovePopup
            companyName={company.name}
            onClose={() => state.setIsAddRemoveWatchlistPopupOpen()}
            onConfirm={() => {
              state.setIsAddRemoveWatchlistPopupOpen();
              profilerProjectStore.addToWatchlist(company);
            }}
          />
        )}
        <div className="company-details" style={style}>
          {!hideBackButton && (
            <button type="button" onClick={() => navigate(-1)} className="backlink">
              {backButtonLabel || 'Back'}
            </button>
          )}

          {state.layout === 'desktop' && (
            <PreHeaderDesktop company={company} windowWidth={utilsStore.windowWidth} />
          )}

          {state.layout === 'mobile' ? (
            <HeaderMobile
              company={company}
              windowWidth={utilsStore.windowWidth}
              mobileHeaderActionButtons={mobileHeaderActionButtons}
            />
          ) : (
            <HeaderDesktop
              company={company}
              isToggleFinancialDownload={financialsState.toggledFinancialDownload}
              linkedInTooltipText={companyLinkedInButtonTooltipText}
              searchTooltipText={
                profilerAccessStore.canViewProfilerDetailsExtendedData(project) &&
                state.isLoadingDetails
                  ? 'Loading financial data'
                  : 'Initiate search based on this company profile'
              }
              isSearchDisabled={
                profilerAccessStore.canViewProfilerDetailsExtendedData(project) &&
                state.isLoadingDetails
              }
              watchlistTooltipContent={watchlistButtonTooltipContent}
              watchlistTooltipPosition={watchlistButtonTooltipPosition}
              watchlistTooltipClickable={watchlistButtonTooltipClickable}
              isWatchlistDisabled={
                profilerProjectStore.isLoadingWatchlist ||
                profilerProjectStore.initialLoadWatchlist ||
                !!profilerProjectStore.companiesInWatchlistAction[company.id] ||
                !profilerAccessStore.canViewProfilerWatchlist
              }
              isInWatchlist={isInWatchlist}
              downloadTooltipContent={<>Download as</>}
              windowWidth={utilsStore.windowWidth}
              downloadPopupProps={downloadPopupProps}
              onWebsiteClick={onWebsiteActionClick}
              onLinkedInClick={onLinkedInActionClick}
              onSearchClick={onSearchActionClick}
              onWatchlistClick={onWatchlistButtonClick}
              onDownloadToggle={onDownloadToggleClick}
              onDownload={handleDownload}
              requestBulkExport={requestBulkExport}
            />
          )}

          <CompanyDetailsTabs
            tabs={tabs}
            currentTab={state.tab}
            similarCompaniesTabId="profiler_search_results_similar_companies_tab"
            companyId={company.id}
            urlContext={tabsUrlContext}
            isStandalone={isStandalonePage}
          />

          {state.tab === DETAILS_POUP_TABS.GENERAL && (
            <GeneralTab
              company={company}
              /* TD-2510: the register court lives on the company-detail payload as
                 snake_case `trade_register_court`; the basic-info read that fills
                 `company` has no court column, so the Summary card cannot reach it
                 from `company` alone. */
              registerCourt={state.companyDetails?.trade_register_court || ''}
              financials={state.financials?.general}
              topManagement={state.topManagement}
              ultimateShareholders={shareholderState.ultimateShareholders}
              isLoadingShareholders={isShareholderGraphLoading}
              shareholdersError={isShareholderGraphFailed ? SHAREHOLDER_GRAPH_ERROR : null}
              isLoadingDetails={state.isLoadingDetails}
              isLoadingSupplementaryData={state.isLoadingSupplementaryData}
              loadDetailsError={state.loadDetailsError}
              loadSupplementaryDataError={state.loadSupplementaryDataError}
              industryCode={resolveIndustryCode(
                company.industryCode,
                utilsStore.options?.[OPTION_KEYS.PROFILER_INDUSTRY_CODES]?.flatList
              )}
              isIndependent={state.isIndependent}
              ownershipSummary={
                (state.ownershipSummaryCompanyId === company.id && state.ownershipSummary) ||
                company.ownershipSummary
              }
              canViewDetails={profilerAccessStore.canViewProfilerDetails(project)}
              canViewExtendedData={profilerAccessStore.canViewProfilerDetailsExtendedData(project)}
              layout={state.layout}
              formatUnit={utilsStore.profilerFinancialsFormatUnit}
              formatCurrency={utilsStore.formatProfilerCurrency}
              companyGoogleMapsLink={companyGoogleMapsLink}
              onRetryLoadDetails={state.loadDetails}
              onRetryShareholders={state.loadShareholderGraph}
              isSimilarCompanyOpen={false}
            />
          )}

          {state.tab === DETAILS_POUP_TABS.PRODUCT_PORTFOLIO && (
            <ProductPortfolioTab
              keyProducts={company.keyProducts}
              businessVerticals={company.businessVerticals}
              products={company.products}
              isLoading={state.isLoadingSupplementaryData}
              hasError={!!state.loadSupplementaryDataError}
            />
          )}

          {(state.tab === DETAILS_POUP_TABS.FINANCIALS ||
            state.tab === DETAILS_POUP_TABS.BANKS ||
            state.tab === DETAILS_POUP_TABS.KEY_CONTACTS) &&
            state.isLoadingDetails && (
              <div className="financials-tab-content">
                <GeneralLoader />
              </div>
            )}

          {(state.tab === DETAILS_POUP_TABS.FINANCIALS ||
            state.tab === DETAILS_POUP_TABS.BANKS ||
            state.tab === DETAILS_POUP_TABS.KEY_CONTACTS) &&
            !!state.loadDetailsError && (
              <div className="financials-tab-content">
                <GeneralError
                  errorMessage={state.loadDetailsError}
                  actionMessage="You may want to:"
                  actionButtontext={'Try again'}
                  onActionButtonClick={state.loadDetails}
                />
              </div>
            )}

          {state.tab === DETAILS_POUP_TABS.FINANCIALS &&
            !state.isLoadingDetails &&
            !state.loadDetailsError && (
              <FinancialsTab
                key={state.observableCompany?.id}
                financials={state.financials}
                isConsolidatedDataEmpty={state.isFinancialsConsolidatedDataEmpty}
                isIndividualDataEmpty={state.isFinancialsIndividualDataEmpty}
                initialScope={financialsState.initialFinancialsScope}
                layout={state.layout}
                formatUnit={utilsStore.profilerFinancialsFormatUnit}
                formatCurrency={utilsStore.formatProfilerCurrency}
              />
            )}

          {state.tab === DETAILS_POUP_TABS.SHAREHOLDER_STRUCTURE && isShareholderGraphLoading && (
            <div className="financials-tab-content">
              <GeneralLoader />
            </div>
          )}

          {state.tab === DETAILS_POUP_TABS.SHAREHOLDER_STRUCTURE && isShareholderGraphFailed && (
            <div className="financials-tab-content">
              <GeneralError
                errorMessage={SHAREHOLDER_GRAPH_ERROR}
                actionMessage="You may want to:"
                actionButtontext={'Try again'}
                onActionButtonClick={state.loadShareholderGraph}
              />
            </div>
          )}

          {state.tab === DETAILS_POUP_TABS.SHAREHOLDER_STRUCTURE &&
            shareholderState.graphStatus === SHAREHOLDER_GRAPH_STATUS.LOADED && (
              <ShareholderStructureTab
                graph={shareholderState.graph}
                figuresStatus={shareholderState.figuresStatus}
                shareholders={shareholderState.ultimateShareholders}
                subsidiaries={state.subsidiaries}
                isIndependent={state.isIndependent}
                companyName={company.name}
                layout={state.layout}
                isPhoneOrTablet={isPhoneOrTablet}
                structureView={shareholderState.structureView}
                onStructureViewChange={shareholderState.setStructureView}
                filters={shareholderState.popupShareholderStructureFilters}
                appliedFilters={shareholderState.appliedGraphFilters}
                validationMessages={
                  shareholderState.validationFieldsPopupShareholderStructure.messages
                }
                showOnSubmitErrorState={
                  shareholderState.onSubmitErrorStatePopupShareholderStructure
                }
                isFilterDisabled={shareholderState.isPopupShareholderStructureFilterDisabled}
                isClearDisabled={shareholderState.isClearFiltersDisabled}
                showFilters={shareholderState.toggledMiniShareholderStructureFilters}
                onFilterChange={shareholderState.setPopupShareholderStructureFiltersValue}
                onApply={shareholderState.onPopupShareholderStructureFilter}
                onClear={shareholderState.onPopupShareholderStructureClearFilter}
                onRemoveFilters={shareholderState.removeAppliedFilters}
                onExpandAllOwners={shareholderState.expandAllOwners}
                onToggleFilters={shareholderState.toggleMiniShareholderStructureFilters}
                onTogglePopup={shareholderState.toggleShareholderPopup}
                onViewShareholderDetails={handleViewShareholderDetails}
                onViewSubsidiaryDetails={handleViewSubsidiaryDetails}
                {...shareholderGraphProps}
              />
            )}

          {state.tab === DETAILS_POUP_TABS.BANKS &&
            !state.isLoadingDetails &&
            !state.loadDetailsError && <BanksTab banks={state.bankConnections} />}

          {state.tab === DETAILS_POUP_TABS.KEY_CONTACTS &&
            !state.isLoadingDetails &&
            !state.loadDetailsError && (
              <KeyContactsTab
                contacts={state.keyContacts}
                companyName={company.name}
                companyId={company.id}
                companyAddress={companyAddress}
                companyGoogleMapsLink={companyGoogleMapsLink}
                companyEmail={state.companyDetails?.email}
                companyPhone={state.companyDetails?.phone}
                layout={state.layout}
                isPhoneOrTablet={isPhoneOrTablet}
                roleFilterOptions={state.contactsRoleFilterOptions}
                countryFilterOptions={state.contactsCountryFilterOptions}
                onViewDetails={handleViewKeyContactDetails}
                onOpenDetailsInNewTab={handleOpenKeyContactInNewTab}
                onBulkRevealComplete={state.loadDetails}
              />
            )}

          {state.tab === DETAILS_POUP_TABS.SIMILAR_COMPANIES && (
            <ComparableCompaniesTab
              state={state}
              similarCompaniesState={similarCompaniesState}
              project={project}
              profilerAccessStore={profilerAccessStore}
              profilerProjectStore={profilerProjectStore}
              utilsStore={utilsStore}
              viewsList={COMPANY_DETAILS_VIEWS_LIST}
              sortOptions={comparableCompaniesSortOptions}
              comparablesSearchLoader={comparablesSearchLoader}
              isActiveTab
              isBackNavigation={isBackNavigation}
            />
          )}

          {state.tab === DETAILS_POUP_TABS.UPDATES && (
            <UpdatesTab
              news={company.news}
              website={company.website}
              now={state.now}
              onTabVisit={() => state.flagUpdateTabVisited(true)}
            />
          )}

          <CompanyContactsDownloadDialog
            isOpen={isContactsDownloadOpen}
            onClose={() => setIsContactsDownloadOpen(false)}
            onDownloaded={state.loadDetails}
            companyId={company.id}
          />
        </div>
      </>
    );
  }
);

export default CompanyDetails;
